import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Spinner } from '../ui/Card';

/**
 * Client-side gate for the customer dashboard — mirrors
 * components/admin/RoleGuard.tsx for the same reasons: this is a UX
 * convenience, not the real security boundary (the Worker's
 * requireAuth middleware on /api/orders etc. is what actually protects
 * customer data; see apps/api/src/middleware/auth-guard.ts).
 *
 * Admins/executives are redirected to /admin instead of seeing an empty
 * customer dashboard — an admin visiting /dashboard almost certainly
 * meant to go to their own area, and there's nothing customer-specific
 * for a staff account to see here (no orders placed as that account).
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated, isAdmin } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner className="h-8 w-8 text-seal-blue" />
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" state={{ redirectTo: '/dashboard' }} replace />;
  if (isAdmin) return <Navigate to="/admin" replace />;

  return <>{children}</>;
}
